import React, { useEffect, useReducer } from 'react';
import { BiLike, BiDislike } from 'react-icons/bi';

import { useIntl } from 'react-intl';
import messages from './messages';

import './LikeDislikeUnit.scss';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');

interface State {
  status: boolean | null,
  created: boolean,
}

interface Action {
  type: string
}

interface Data {
  data: {
    attributes: {
      status: boolean
    }
  }
}

/**
 * Function that specifies how the state gets updated.
 * @param  {State} state   Current state.
 * @param  {Action} action Describes kind of action to be executed.
 * @return {Object}        Updated state based on the action type.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'like':
      return {
        created: true,
        status: true,
      };
    case 'dislike':
      return {
        created: true,
        status: false,
      };
    case 'created':
      return { ...state, created: true };
    default:
      return { ...state, status: null };
  }
}

/**
 * Function that gets the remote status from the backend side, this function will set the
 * local state based on the response data.
 * @param  {React.Dispatch} dispatch  Functions that sets the state.
 * @param  {String}         unitId    Unit identifier as string.
 */
function getStatus(dispatch: React.Dispatch<Action>, unitId: string) {
  const apiConfig = { headers: { accept: 'application/vnd.api+json,application/json' } };
  const { COURSE_EXPERIENCE_API_URL } = getConfig();
  const url = `${COURSE_EXPERIENCE_API_URL}/like/units/${unitId}/`;
  let action = 'created';

  getAuthenticatedHttpClient().get(url, apiConfig).then(({ data }: { data: Data }) => {
    const { data: { attributes: { status } } } = data;

    if (typeof status === 'boolean') {
      action = status ? 'like' : 'dislike';
    }

    dispatch({ type: action });
  }, (error: Error) => {
    // eslint-disable-next-line no-console
    console.error('Error fetching like-dislike status', error.message);
  });
}

/**
 * Function that sets a new status in the backend side, this function will set the
 * local state based on the parameters.
 * If the new status is the same as the state.status the status will be set to null<revert action>
 * @param  {React.Dispatch} dispatch  Functions that sets the state.
 * @param  {State}          state     Current local state.
 * @param  {String}         courseId  Course identifier as string.
 * @param  {String}         unitId    Unit identifier as string.
 * @param  {Boolean}        status    New value of the status.
 */
function setStatus(dispatch: React.Dispatch<Action>, state: State, courseId: string, unitId: string, status: boolean) {
  let url = `${getConfig().COURSE_EXPERIENCE_API_URL}/like/units/`;
  let request = getAuthenticatedHttpClient().post;
  const newStatus = state.status === status ? null : status;
  let action = 'default';
  const body = {
    status: newStatus,
    item_id: unitId,
    course_id: { type: 'CourseOverview', id: courseId },
  };
  const headers = { headers: { accept: 'application/vnd.api+json,application/json' } };

  if (state.created) {
    request = getAuthenticatedHttpClient().patch;
    url = `${url}${unitId}/`;
  }

  if (typeof newStatus === 'boolean') {
    action = newStatus ? 'like' : 'dislike';
  }

  dispatch({ type: action });

  request(url, body, headers).then(() => {
    // Any action is not required
  }, (error: Error) => {
    dispatch({ type: 'default' });
    // eslint-disable-next-line no-console
    console.error('Unable to set like-dislike status with error:', error.message);
  });
}

/**
 * Function react component that allows to set like or dislike or neither. This allow to select
 * thumbs up or thumb down.
 * @param  {String}         courseId  Course identifier as string.
 * @param  {String}         unitId    Unit identifier as string.
 * @return {JSX.Element}              React element.
 */
function LikeDislikeUnit({ unitId, courseId }: { unitId: string, courseId: string }): JSX.Element {
  const initialState = {
    status: null,
    created: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const intl = useIntl();

  useEffect(() => {
    getStatus(dispatch, unitId);
  }, [unitId]);

  return (
    <div className="like-dislike-container">
      <button
        id="dislike-button"
        type="button"
        className={state.status === false ? 'like-dislike-active' : 'like-dislike-inactive'}
        onClick={() => setStatus(dispatch, state, courseId, unitId, false)}
      >
        {intl.formatMessage(messages.dislike)}
        <BiDislike />
      </button>
      <button
        id="like-button"
        type="button"
        className={state.status === true ? 'like-dislike-active' : 'like-dislike-inactive'}
        onClick={() => setStatus(dispatch, state, courseId, unitId, true)}
      >
        {intl.formatMessage(messages.like)}
        <BiLike />
      </button>
    </div>
  );
}

export default LikeDislikeUnit;
